import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '../services/api/auth';
import { api } from '../services/api/client';
import { useUser } from './useUser';
import { isEmail, matches, passwordComplexity, required } from '../utils/validators';

type RegistrationFieldErrors = {
  trackingNumber: string[];
  name: string[];
  email: string[];
  password: string[];
  passwordConfirmation: string[];
};

const createEmptyFieldErrors = (): RegistrationFieldErrors => ({
  trackingNumber: [],
  name: [],
  email: [],
  password: [],
  passwordConfirmation: []
});

const initialTrackingNumber = (value: string | null): string =>
  value ? value.trim().toUpperCase() : '';

export const useRegistration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<'tracking' | 'form'>('tracking');
  const [trackingNumber, setTrackingNumber] = useState(() =>
    initialTrackingNumber(searchParams.get('tracking')),
  );
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');

  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<RegistrationFieldErrors>(createEmptyFieldErrors());
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const validate = () => {
    const nextFieldErrors = createEmptyFieldErrors();
    const nextFormErrors: string[] = [];

    if (step === 'tracking') {
      const trackingError = required(trackingNumber, 'auth.registration.validation.tracking_required');
      if (trackingError) {
        nextFieldErrors.trackingNumber.push(trackingError);
        nextFormErrors.push(trackingError);
      }
    } else {
      const nameError = required(name, 'auth.registration.validation.name_required');
      if (nameError) {
        nextFieldErrors.name.push(nameError);
        nextFormErrors.push(nameError);
      }

      const emailRequiredError = required(email, 'auth.registration.validation.email_required');
      if (emailRequiredError) {
        nextFieldErrors.email.push(emailRequiredError);
        nextFormErrors.push(emailRequiredError);
      } else {
        const emailError = isEmail(email, 'auth.registration.validation.email_invalid');
        if (emailError) {
          nextFieldErrors.email.push(emailError);
          nextFormErrors.push(emailError);
        }
      }

      const passwordError = passwordComplexity(password, 'auth.registration.validation.password_complexity');
      if (passwordError) {
        nextFieldErrors.password.push(passwordError);
        nextFormErrors.push(passwordError);
      }

      const confirmationError = matches(
        passwordConfirmation,
        password,
        'auth.registration.validation.password_mismatch',
      );
      if (confirmationError) {
        nextFieldErrors.passwordConfirmation.push(confirmationError);
        nextFormErrors.push(confirmationError);
      }

    }

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);
    setError('');

    return nextFormErrors.length === 0;
  };

  const validateMutation = useMutation({
    mutationFn: () => authApi.validateTrackingCode(trackingNumber),
    onSuccess: (response) => {
      if (response.valid) {
        if (response.already_registered) {
          const messageKey = 'auth.registration.already_registered';
          setError(t(messageKey));
          setFormErrors([messageKey]);
        } else {
          setEmail(response.email || '');
          setName(response.contact_name || '');
          setFieldErrors(createEmptyFieldErrors());
          setFormErrors([]);
          setError('');
          setStep('form');
        }
      } else {
        const messageKey = response.error || 'auth.registration.invalid_code';
        setError(t(messageKey));
        setFormErrors([messageKey]);
      }
    },
    onError: (err) => {
      const message = (err as Error).message;
      setError(message);
      setFormErrors([message]);
    },
  });

  const registerMutation = useMutation({
    mutationFn: () => authApi.register({
      tracking_number: trackingNumber,
      password,
      password_confirmation: passwordConfirmation,
      name,
    }),
    onSuccess: (response) => {
      api.setToken(response.token);
      setUser(response.user);
      setFieldErrors(createEmptyFieldErrors());
      setFormErrors([]);
      setError('');
      navigate('/dashboard');
    },
    onError: (err) => {
      const message = (err as Error).message;
      setError(message);
      setFormErrors([message]);
    },
  });

  const handleTrackingSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    validateMutation.mutate();
  };

  const handleRegistrationSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    registerMutation.mutate();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    switch (name) {
      case 'trackingNumber':
        setTrackingNumber(value.toUpperCase());
        break;
      case 'name':
        setName(value);
        break;
      case 'password':
        setPassword(value);
        break;
      case 'passwordConfirmation':
        setPasswordConfirmation(value);
        break;
    }
  };

  const loading = validateMutation.isPending || registerMutation.isPending;

  return {
    step, setStep,
    trackingNumber, email, name, password, passwordConfirmation,
    loading, error,
    fieldErrors,
    formErrors,
    validate,
    handleTrackingSubmit, handleRegistrationSubmit, handleChange
  };
};
